import { widgetRegionAtom } from '@/atoms/integrationAuthAtoms';
import { useAtomValue } from 'jotai';
import Image from 'next/image';
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  PopoverDrawer,
  PopoverDrawerContent,
  PopoverDrawerTrigger,
} from '@/components/ui/monbile-responsive-popover-drawer';
import { useLocalizedCountries } from '@/hooks/useLocalizedCountries';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import { countriesToRegion } from '@/lists/countriesToRegion';
import { ComboboxTrigger } from '@heidi/design-system/combobox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@heidi/design-system/command';
import { DrawerHeader, DrawerTitle } from '@heidi/design-system/drawer';
import { ScrollArea } from '@heidi/design-system/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { TypographyP2 } from '@heidi/design-system/typography';
import { Region } from '@heidi/schema/ml-scribe';
import { type CountryType, sortedCountries } from '@heidi/utils/countries';

import { personalizationSharedMessages } from '../personalization-shared.messages';

type Props = {
  value?: CountryType;
  setValue: (value: CountryType) => void;
  className?: string;
  popoverClassName?: string;
  disabled?: boolean;
  drawerTitle?: string;
};

export const OnboardingCountrySelect = ({
  setValue,
  value,
  className,
  popoverClassName,
  disabled,
  drawerTitle,
}: Props) => {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const { localizedCountries, getCountryLocalizedName } = useLocalizedCountries();
  const widgetRegion = useAtomValue(widgetRegionAtom);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchValue = useDeferredValue(search.toLowerCase());
  const selectedCountry = value;
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const selectedCountryLocalizedName = selectedCountry
    ? getCountryLocalizedName(selectedCountry)
    : null;

  const checkTruncation = useCallback(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, []);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    checkTruncation();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => checkTruncation());
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkTruncation, selectedCountryLocalizedName]);

  const filteredCountries = useMemo(() => {
    const validWidgetRegion =
      widgetRegion &&
      Object.values(Region)
        .map((x) => x.toString())
        .includes(widgetRegion.toUpperCase());

    return validWidgetRegion
      ? localizedCountries.filter(
          (country) =>
            countriesToRegion[country.name]?.toUpperCase() === widgetRegion.toUpperCase()
        )
      : localizedCountries;
  }, [widgetRegion, localizedCountries]);

  const searchedCountries = filteredCountries.filter(
    (country) =>
      country.name.toLowerCase().includes(searchValue) ||
      country.localizedName.toLowerCase().includes(searchValue)
  );

  const displayCountries = [...searchedCountries];

  const onCountrySelected = (val: string) => {
    const countrySelected = sortedCountries.find((ctry) => ctry.name === val);
    if (countrySelected) {
      setValue(countrySelected);
    }
    setOpen(false);
    setSearch('');
  };

  return (
    <div className={cn('flex w-full flex-col', className)}>
      <div className="w-full">
        <PopoverDrawer open={open} onOpenChange={setOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverDrawerTrigger asChild>
                <ComboboxTrigger
                  open={open}
                  disabled={disabled}
                  isPlaceholder={!selectedCountry}
                  data-testid={'country-select'}
                  role="combobox"
                >
                  <div className="flex min-w-0 items-center gap-4">
                    {selectedCountry?.countryCode && (
                      <Image
                        src={`/country-flags/3x2/${selectedCountry?.countryCode?.toUpperCase()}.svg`}
                        width={24}
                        height={16}
                        alt={selectedCountryLocalizedName || selectedCountry?.name}
                        className="h-[16px] w-[24px] rounded-sm"
                      />
                    )}
                    <TypographyP2 ref={textRef} className="truncate">
                      {selectedCountryLocalizedName ||
                        intl.formatMessage(personalizationSharedMessages.selectCountry)}
                    </TypographyP2>
                  </div>
                </ComboboxTrigger>
              </PopoverDrawerTrigger>
            </TooltipTrigger>
            {isTruncated && !open && selectedCountryLocalizedName && (
              <TooltipContent side="bottom">
                {selectedCountryLocalizedName}
              </TooltipContent>
            )}
          </Tooltip>
          <PopoverDrawerContent
            className={cn(
              isMobile
                ? 'px-0 pb-4'
                : 'max-h-[--radix-popover-content-available-height] w-[--radix-popover-trigger-width] p-0',
              popoverClassName
            )}
            sideOffset={4}
            align="start"
            side="bottom"
          >
            {isMobile && (
              <DrawerHeader>
                <DrawerTitle>
                  {drawerTitle ??
                    intl.formatMessage(personalizationSharedMessages.selectCountry)}
                </DrawerTitle>
              </DrawerHeader>
            )}
            <Command shouldFilter={false}>
              <CommandInput
                data-testid="county-select-input"
                placeholder={intl.formatMessage(
                  personalizationSharedMessages.searchForCountry
                )}
                onValueChange={(e) => setSearch(e)}
              />
              <CommandEmpty>
                <FormattedMessage {...personalizationSharedMessages.noCountryFound} />
              </CommandEmpty>
              <ScrollArea
                className={
                  isMobile
                    ? 'max-h-[60vh] overflow-auto'
                    : 'max-h-[220px] overflow-scroll sm:overflow-auto'
                }
              >
                <CommandGroup>
                  {displayCountries.map((country) => (
                    <CommandItem
                      data-testid="country-select-item"
                      className="flex gap-3"
                      key={country.name}
                      value={country.name}
                      onSelect={() => {
                        onCountrySelected(country.name);
                      }}
                    >
                      {country.countryCode && (
                        <Image
                          src={`/country-flags/3x2/${country.countryCode.toUpperCase()}.svg`}
                          width={24}
                          height={16}
                          alt={country.localizedName}
                          className="h-[16px] w-[24px] rounded-sm"
                        />
                      )}
                      {country.localizedName}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </ScrollArea>
            </Command>
          </PopoverDrawerContent>
        </PopoverDrawer>
      </div>
    </div>
  );
};
